import type { PostContent } from '@/types/board';

/**
 * TipTap JSON 내용을 HTML로 변환하는 함수
 * 
 * TipTap 에디터에서 생성된 JSON 형태의 콘텐츠를
 * 표준 HTML 문자열로 변환합니다. 모든 TipTap 노드 타입을 지원하며,
 * 스타일과 속성을 적절한 HTML로 매핑합니다.
 * 
 * @param content - 변환할 TipTap JSON 내용 또는 HTML 문자열
 * @returns HTML 문자열
 * 
 * @example
 * ```typescript
 * const html = tiptapToHtml(tiptapContent);
 * document.getElementById('content').innerHTML = html;
 * ```
 */
export function tiptapToHtml(content: PostContent | string | null): string {
  if (!content) return '';

  if (typeof content === 'string') {
    return content;
  }

  if (content.type === 'doc' && content.content) {
    return convertNodesToHtml(content.content);
  }

  return '';
}

/**
 * TipTap 노드들을 HTML로 변환하는 재귀 함수
 * 
 * TipTap 노드 배열을 순회하면서 각 노드 타입에 맞는 HTML을 생성합니다.
 * 중첩된 구조를 재귀적으로 처리하여 완전한 HTML 문서를 만듭니다.
 * 
 * @param nodes - 변환할 TipTap 노드 배열
 * @returns HTML 문자열
 * 
 * @example
 * ```typescript
 * const html = convertNodesToHtml(tiptapNodes);
 * console.log('Generated HTML:', html);
 * ```
 */
function convertNodesToHtml(nodes: Array<{
  type: string;
  text?: string;
  content?: Array<{
    type: string;
    text?: string;
    marks?: Array<{
      type: string;
      attrs?: Record<string, unknown>;
    }>;
  }>;
  marks?: Array<{
    type: string;
    attrs?: Record<string, unknown>;
  }>;
  attrs?: Record<string, unknown>;
}>): string {
  if (!Array.isArray(nodes)) return '';

  return nodes.map(node => {
    if (node.type === 'text') {
      let text = node.text || '';
      text = text.replace(/\n/g, '<br>');

      // 텍스트 마크(굵게, 기울임, 밑줄 등) 처리
      if (node.marks && Array.isArray(node.marks)) {
        node.marks.forEach((mark: { type: string; attrs?: Record<string, unknown> }) => {
          switch (mark.type) {
            case 'bold':
            case 'strong':
              text = `<strong>${text}</strong>`;
              break;
            case 'italic':
            case 'em':
              text = `<em>${text}</em>`;
              break;
            case 'strike':
              text = `<del>${text}</del>`;
              break;
            case 'underline':
              text = `<u>${text}</u>`;
              break;
            case 'code':
              text = `<code>${text}</code>`;
              break;
            case 'link':
              const href = mark.attrs?.href || '#';
              text = `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`;
              break;
            case 'textStyle':
              const color = mark.attrs?.color;
              if (color) {
                text = `<span style="color: ${color}">${text}</span>`;
              }
              break;
            case 'color':
              const colorValue = mark.attrs?.color;
              if (colorValue) {
                text = `<span style="color: ${colorValue}">${text}</span>`;
              }
              break;
            case 'highlight':
              const backgroundColor = mark.attrs?.color;
              if (backgroundColor) {
                text = `<mark style="background-color: ${backgroundColor}">${text}</mark>`;
              } else {
                text = `<mark>${text}</mark>`;
              }
              break;
          }
        });
      }

      return text;
    }

    // 블록 레벨 노드 타입별 HTML 생성
    switch (node.type) {
      case 'paragraph': {
        const paragraphAttrs = node.attrs || {};
        const paragraphStyle = buildInlineStyle(paragraphAttrs);
        const paragraphClass = paragraphAttrs.class || '';
        const paragraphContent = convertNodesToHtml(node.content || []);
        const finalContent = paragraphContent.trim() === '' ? '<br>' : paragraphContent;
        return `<p${paragraphStyle}${paragraphClass ? ` class="${paragraphClass}"` : ''}>${finalContent}</p>`;
      }

      case 'heading':
        const headingAttrs = node.attrs || {};
        const headingStyle = buildInlineStyle(headingAttrs);
        const headingClass = headingAttrs.class || '';
        const level = (headingAttrs.level as number) || 1;
        return `<h${level}${headingStyle}${headingClass ? ` class="${headingClass}"` : ''}>${convertNodesToHtml(node.content || [])}</h${level}>`;

      case 'bulletList':
        const bulletListAttrs = node.attrs || {};
        const bulletListStyle = buildInlineStyle(bulletListAttrs);
        const bulletListClass = bulletListAttrs.class || '';
        return `<ul${bulletListStyle}${bulletListClass ? ` class="${bulletListClass}"` : ''}>${convertNodesToHtml(node.content || [])}</ul>`;

      case 'orderedList':
        const orderedListAttrs = node.attrs || {};
        const orderedListStyle = buildInlineStyle(orderedListAttrs);
        const orderedListClass = orderedListAttrs.class || '';
        return `<ol${orderedListStyle}${orderedListClass ? ` class="${orderedListClass}"` : ''}>${convertNodesToHtml(node.content || [])}</ol>`;

      case 'listItem':
        const listItemAttrs = node.attrs || {};
        const listItemStyle = buildInlineStyle(listItemAttrs);
        const listItemClass = listItemAttrs.class || '';
        return `<li${listItemStyle}${listItemClass ? ` class="${listItemClass}"` : ''}>${convertNodesToHtml(node.content || [])}</li>`;

      case 'blockquote':
        const blockquoteAttrs = node.attrs || {};
        const blockquoteStyle = buildInlineStyle(blockquoteAttrs);
        const blockquoteClass = blockquoteAttrs.class || '';
        return `<blockquote${blockquoteStyle}${blockquoteClass ? ` class="${blockquoteClass}"` : ''}>${convertNodesToHtml(node.content || [])}</blockquote>`;

      case 'codeBlock':
        const codeBlockAttrs = node.attrs || {};
        const codeBlockStyle = buildInlineStyle(codeBlockAttrs);
        const codeBlockClass = codeBlockAttrs.class || '';
        const language = codeBlockAttrs.language || '';
        return `<pre${codeBlockStyle}${codeBlockClass ? ` class="${codeBlockClass}"` : ''}><code class="language-${language}">${convertNodesToHtml(node.content || [])}</code></pre>`;

      case 'horizontalRule':
        return '<hr>';

      case 'image':
        const src = node.attrs?.src || '';
        const alt = node.attrs?.alt || '';
        const imageAttrs = node.attrs || {};
        const imageStyle = buildInlineStyle(imageAttrs);
        const imageClass = imageAttrs.class || 'max-w-full h-auto';

        return `<img src="${src}" alt="${alt}" class="${imageClass}"${imageStyle} />`;

      case 'table':
        const tableAttrs = node.attrs || {};
        const tableStyle = buildInlineStyle(tableAttrs);
        const tableClass = tableAttrs.class || '';
        return `<table${tableStyle}${tableClass ? ` class="${tableClass}"` : ''}>${convertNodesToHtml(node.content || [])}</table>`;

      case 'tableRow':
        const rowAttrs = node.attrs || {};
        const rowStyle = buildInlineStyle(rowAttrs);
        const rowClass = rowAttrs.class || '';
        return `<tr${rowStyle}${rowClass ? ` class="${rowClass}"` : ''}>${convertNodesToHtml(node.content || [])}</tr>`;

      case 'tableHeader':
        const headerAttrs = node.attrs || {};
        const headerStyle = buildInlineStyle(headerAttrs);
        const headerClass = headerAttrs.class || '';

        // 테이블 헤더 셀의 colspan, rowspan 속성 처리
        let headerAdditionalAttrs = '';
        if (headerAttrs.colspan && (headerAttrs.colspan as number) > 1) {
          headerAdditionalAttrs += ` colspan="${headerAttrs.colspan}"`;
        }
        if (headerAttrs.rowspan && (headerAttrs.rowspan as number) > 1) {
          headerAdditionalAttrs += ` rowspan="${headerAttrs.rowspan}"`;
        }

        return `<th${headerStyle}${headerClass ? ` class="${headerClass}"` : ''}${headerAdditionalAttrs}>${convertNodesToHtml(node.content || [])}</th>`;

      case 'tableCell':
        const cellAttrs = node.attrs || {};
        const cellStyle = buildInlineStyle(cellAttrs);
        const cellClass = cellAttrs.class || '';

        // 테이블 셀의 colspan, rowspan 속성 처리
        let additionalAttrs = '';
        if (cellAttrs.colspan && (cellAttrs.colspan as number) > 1) {
          additionalAttrs += ` colspan="${cellAttrs.colspan}"`;
        }
        if (cellAttrs.rowspan && (cellAttrs.rowspan as number) > 1) {
          additionalAttrs += ` rowspan="${cellAttrs.rowspan}"`;
        }

        return `<td${cellStyle}${cellClass ? ` class="${cellClass}"` : ''}${additionalAttrs}>${convertNodesToHtml(node.content || [])}</td>`;

      case 'hardBreak':
        return '<br>';

      default:
        // 알 수 없는 노드 타입의 경우 하위 콘텐츠만 처리
        if (node.content) {
          return convertNodesToHtml(node.content);
        }
        return '';
    }
  }).join('');
}

/**
 * 노드의 속성을 인라인 스타일로 변환하는 함수
 * 
 * TipTap 노드의 속성들을 CSS 인라인 스타일 문자열로 변환합니다.
 * width, height, textAlign 등 기본 속성과 사용자 정의 스타일을 처리합니다.
 * 
 * @param attrs - 변환할 노드 속성 객체
 * @returns CSS 인라인 스타일 문자열 (예: ' style="width: 100px; color: red;"')
 * 
 * @example
 * ```typescript
 * const style = buildInlineStyle({ width: '100px', color: 'red' });
 * console.log(style); // ' style="width: 100px; color: red;"'
 * ```
 */
function buildInlineStyle(attrs: Record<string, unknown>): string {
  if (!attrs) return '';

  const styles: string[] = [];

  // 기본 크기 및 정렬 속성 처리
  if (attrs.width) {
    styles.push(`width: ${attrs.width}`);
  }

  if (attrs.colwidth && Array.isArray(attrs.colwidth) && (attrs.colwidth as unknown[]).length > 0) {
    styles.push(`width: ${(attrs.colwidth as unknown[])[0]}px`);
  }

  if (attrs.height) {
    styles.push(`height: ${attrs.height}`);
  }

  if (attrs.textAlign) {
    styles.push(`text-align: ${attrs.textAlign}`);
  }

  // 지원하는 CSS 속성 목록 (camelCase -> kebab-case 변환)
  const styleProperties = [
    'background', 'backgroundColor', 'border', 'borderColor', 'borderWidth', 'borderStyle',
    'borderTop', 'borderRight', 'borderBottom', 'borderLeft',
    'padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
    'margin', 'marginTop', 'marginRight', 'marginBottom', 'marginLeft',
    'fontSize', 'fontWeight', 'fontFamily', 'color', 'lineHeight',
    'display', 'position', 'top', 'right', 'bottom', 'left',
    'minWidth', 'maxWidth', 'minHeight', 'maxHeight', 'overflow',
    'textDecoration', 'textTransform', 'letterSpacing', 'wordSpacing',
    'verticalAlign', 'whiteSpace', 'opacity', 'zIndex'
  ].filter(prop => !['width', 'height', 'textAlign'].includes(prop));

  // 각 CSS 속성을 kebab-case로 변환하여 스타일 배열에 추가
  styleProperties.forEach(prop => {
    if (attrs[prop]) {
      const cssProp = prop.replace(/([A-Z])/g, '-$1').toLowerCase();
      styles.push(`${cssProp}: ${attrs[prop] as string}`);
    }
  });

  // 사용자 정의 스타일 속성 처리
  if (attrs.style && typeof attrs.style === 'string') {
    if ((attrs.style as string).includes(':')) {
      styles.push(attrs.style as string);
    } else {
      styles.push(attrs.style as string);
    }
  }

  // 스타일이 있는 경우에만 style 속성 반환
  if (styles.length > 0) {
    const styleString = ` style="${styles.join('; ')}"`;
    return styleString;
  }

  return '';
}

/**
 * TipTap 내용을 간단한 텍스트로 변환하는 함수 (검색용)
 * 
 * TipTap JSON 콘텐츠에서 모든 마크업을 제거하고 순수 텍스트만 추출합니다.
 * 검색 기능이나 텍스트 분석에서 사용할 수 있습니다.
 * 
 * @param content - 변환할 TipTap JSON 내용 또는 HTML 문자열
 * @returns 순수 텍스트 (마크업 제거됨)
 * 
 * @example
 * ```typescript
 * const text = tiptapToText(tiptapContent);
 * const searchIndex = text.toLowerCase().indexOf(searchTerm);
 * ```
 */
export function tiptapToText(content: PostContent | string | null): string {
  if (!content) return '';

  if (typeof content === 'string') {
    return content;
  }

  if (content.type === 'doc' && content.content) {
    return extractTextFromNodes(content.content);
  }

  return '';
}

/**
 * 노드들에서 텍스트만 추출하는 재귀 함수
 * 
 * TipTap 노드 배열을 순회하면서 텍스트 노드의 내용만 추출합니다.
 * 중첩된 구조를 재귀적으로 처리하여 모든 텍스트를 수집합니다.
 * 
 * @param nodes - 텍스트를 추출할 TipTap 노드 배열
 * @returns 추출된 순수 텍스트 (공백으로 구분됨)
 * 
 * @example
 * ```typescript
 * const text = extractTextFromNodes(tiptapNodes);
 * console.log('Extracted text:', text);
 * ```
 */
function extractTextFromNodes(nodes: Array<{ type: string; text?: string; content?: unknown[] }>): string {
  if (!Array.isArray(nodes)) return '';

  return nodes.map(node => {
    if (node.type === 'text') {
      return node.text || '';
    }

    if (node.content) {
      return extractTextFromNodes(node.content as Array<{ type: string; text?: string; content?: unknown[] }>);
    }

    return '';
  }).join(' ');
}

/**
 * TipTap 노드 유틸리티 함수들
 * 
 * TipTap 에디터의 노드 조작과 관련된 유틸리티 함수들을 제공합니다.
 */

/**
 * TipTap 노드의 타입을 반환하는 함수
 * 
 * @param node - 타입을 확인할 TipTap 노드
 * @returns 노드 타입 문자열
 */
export const getNodeType = (node: {
  type: string;
  attrs?: Record<string, unknown>;
  content?: unknown[];
  marks?: Array<{
    type: string;
    attrs?: Record<string, unknown>;
  }>;
  text?: string;
}): string => {
  return node.type;
};

/**
 * TipTap 노드의 속성을 반환하는 함수
 * 
 * @param node - 속성을 확인할 TipTap 노드
 * @returns 노드 속성 객체 또는 undefined
 */
export const getNodeAttributes = (node: {
  type: string;
  attrs?: Record<string, unknown>;
  content?: unknown[];
  marks?: Array<{
    type: string;
    attrs?: Record<string, unknown>;
  }>;
  text?: string;
}): Record<string, unknown> | undefined => {
  return node.attrs;
};

/**
 * 테이블 행을 생성하는 함수
 * 
 * 지정된 개수의 빈 셀을 가진 테이블 행을 생성합니다.
 * 새로운 테이블을 만들 때 기본 구조를 제공하는 데 사용됩니다.
 * 
 * @param cellCount - 생성할 셀의 개수
 * @returns 테이블 행 노드 객체
 * 
 * @example
 * ```typescript
 * const row = createTableRow(3); // 3개 셀을 가진 행 생성
 * console.log(row.type); // 'tableRow'
 * ```
 */
export const createTableRow = (cellCount: number): {
  type: string;
  content: Array<{
    type: string;
    content: Array<{
      type: string;
      text: string;
    }>;
  }>;
} => {
  const cells = Array.from({ length: cellCount }, () => ({
    type: 'tableCell',
    content: [{ type: 'paragraph', text: '' }],
  }));

  return {
    type: 'tableRow',
    content: cells,
  };
};

/**
 * 테이블 셀을 생성하는 함수
 * 
 * 지정된 텍스트를 포함하는 테이블 셀을 생성합니다.
 * 테이블 편집 시 새로운 셀을 추가하는 데 사용됩니다.
 * 
 * @param text - 셀에 포함할 텍스트
 * @returns 테이블 셀 노드 객체
 * 
 * @example
 * ```typescript
 * const cell = createTableCell('샘플 텍스트');
 * console.log(cell.type); // 'tableCell'
 * ```
 */
export const createTableCell = (text: string): {
  type: string;
  content: Array<{
    type: string;
    text: string;
  }>;
} => {
  return {
    type: 'tableCell',
    content: [{ type: 'paragraph', text }],
  };
}; 
